import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {desktopAndUpLinaria} from '../../../constants/customMedia';
import {RADIUS, SPACING} from '../../../constants/sizing';
import {Icon} from '../../atoms/Icon/Icon';
import {IconName} from '../../atoms/Icon/IconName';

interface MessageBoxProps {
  icon: IconName;
  backgroundColor: keyof typeof COLORS;
  iconColor?: keyof typeof COLORS;
  'data-testid'?: string;
  className?: string;
  isResponsive?: boolean;
}

export const MessageBox = ({
  backgroundColor,
  icon,
  'data-testid': dataTestId,
  children,
  className,
  iconColor,
  isResponsive = true,
}: React.PropsWithChildren<MessageBoxProps>) => {
  return (
    <Container
      data-testid={dataTestId}
      className={className}
      $backgroundColor={backgroundColor}
      data-is-responsive={isResponsive}
    >
      <IconWrapper
        $backgroundColor={backgroundColor}
        $iconColor={iconColor || 'Black'}
      >
        <StyledIcon asset={icon} fill={COLORS[iconColor ?? 'Black']} />
      </IconWrapper>
      {children}
    </Container>
  );
};

const IconWrapper = styled.div<{
  $backgroundColor: keyof typeof COLORS;
  $iconColor: keyof typeof COLORS;
}>`
  border-radius: 100%;
  background-color: ${props => COLORS[props.$backgroundColor]};
  height: 3rem;
  width: 3rem;
  box-sizing: content-box;
  border: 4px solid ${COLORS.White};
  position: absolute;
  // icon size - container padding
  top: -1.75rem;

  display: flex;
  align-items: center;
  justify-content: center;

  [data-is-responsive='true'] & {
    ${desktopAndUpLinaria(`
      position: unset;
      top: unset;
      border: unset;

      height: 3.5rem;
      width: 3.5rem;
    `)}
  }
`;

const StyledIcon = styled(Icon)`
  width: 2rem;
  height: 2rem;
  flex-basis: 2rem;

  ${desktopAndUpLinaria(`
    width: 3.5rem;
    height: 3.5rem;
    flex-basis: 3.5rem;
  `)}
`;

const Container = styled.div<{
  $backgroundColor: keyof typeof COLORS;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.5rem;
  background-color: ${props => COLORS[props.$backgroundColor]};
  padding: 2rem 0 1rem 0;
  border-radius: ${RADIUS.Md};
  position: relative;
  margin-top: ${SPACING['Y.Xl']};

  &[data-is-responsive='true'] {
    ${desktopAndUpLinaria(`
      flex-direction: row;
      padding: 2rem 3rem 2rem 3.5rem;
      text-align: unset;
      margin-top: unset;
    `)}
  }
`;
